import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import ReactTable from 'react-table'
import { Button } from 'reactstrap'
import moment from 'moment'
import { translations } from 'helpers/react-table'
import { decimalFormat } from 'helpers/formater'
import { getVisibleContracts } from 'reducers/contracts'
import {
  fetchContractsTransactions,
  getTableData,
  getIsLoading,
  getSelectedContractId
} from 'store/deals'
import { TableSpinner } from 'components/common/Spinner'
import { isNil } from 'lodash'

const headerStyle = {
  textTransform: 'uppercase',
}

const columnLeft = {
  headerStyle,
}

const columnRight = {
  headerStyle,
  Cell: (row) => <div style={{ textAlign: 'right' }}>{row.value}</div>,
}

const validText = (status) => {
  const { t } = useTranslation()
  if(status == 1)
    return t('execution.transactionStatus.pending')
  if (status == 2)
    return t('execution.transactionStatus.validatedInternal')
  if (status == 3)
    return t('execution.transactionStatus.invalidatedInternal')
  if (status == 4)
    return t('execution.transactionStatus.validatedClient')
}

const DealsTable = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const visibleContracts = useSelector(getVisibleContracts)
  let data = useSelector(getTableData)
  data = data.filter(function (element) {
    return element !== undefined
  })
  const isLoading = useSelector(getIsLoading)

  const selectedContractId = useSelector(getSelectedContractId)
  const [selected, setSelected] = React.useState(null)

  useEffect(() => {
    const filtered = data.filter((item) => item && Number(item.visualize.contractId) === selectedContractId)
    if (filtered.length > 0 && !filtered.some((item) => item.id === selected)) {
      setSelected(filtered[0]?.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selected])

  useEffect(() => {
    dispatch(fetchContractsTransactions(visibleContracts))
  }, [dispatch, visibleContracts])

  const columns = [
    {
      Header: t('deals.table.contract'),
      accessor: 'contract',
      ...columnLeft,
      Cell: (row) => (
        <div>
          <span style={{ marginRight: 10 }}>
            {row.original.nrjId == 2 ?
              <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#00aaff"><path d="m422-232 207-248H469l29-227-185 267h139l-30 208ZM320-80l40-280H160l360-520h80l-40 320h240L400-80h-80Zm151-390Z" /></svg>
              :
              <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#008480"><path d="M240-400q0 52 21 98.5t60 81.5q-1-5-1-9v-9q0-32 12-60t35-51l113-111 113 111q23 23 35 51t12 60v9q0 4-1 9 39-35 60-81.5t21-98.5q0-50-18.5-94.5T648-574q-20 13-42 19.5t-45 6.5q-62 0-107.5-41T401-690q-39 33-69 68.5t-50.5 72Q261-513 250.5-475T240-400Zm240 52-57 56q-11 11-17 25t-6 29q0 32 23.5 55t56.5 23q33 0 56.5-23t23.5-55q0-16-6-29.5T537-292l-57-56Zm0-492v132q0 34 23.5 57t57.5 23q18 0 33.5-7.5T622-658l18-22q74 42 117 117t43 163q0 134-93 227T480-80q-134 0-227-93t-93-227q0-129 86.5-245T480-840Z" /></svg>}
          </span>
          {row.value}
        </div>
      ),
    },
    {
      Header: t('deals.table.businessUnit'),
      accessor: 'businessUnit',
      ...columnLeft,
      width: 120,
    },
    {
      Header: t('deals.table.parcel'),
      accessor: 'parcel',
      ...columnLeft,
      width: 80,
    },
    {
      Header: t('deals.table.product'),
      accessor: 'product',
      ...columnLeft,
    },
    {
      Header: t('deals.table.date'),
      accessor: 'date',
      width: 120,
      ...columnLeft,
      Cell: (row) => <div style={{ textAlign: 'center' }}>{row.original.dateFormatted}</div>,
      filterMethod: (filter, row) => {
        const a = String(row._original.dateFormatted)
        const b = filter.value
        return a.includes(b)
      },
      sortMethod: (a, b, desc) => {
        // force null and undefined to the bottom
        a = a === null || a === undefined ? '' : moment(a).format('x')
        b = b === null || b === undefined ? '' : moment(b).format('x')
        return a > b ? 1 : a < b ? -1 : 0
      },
    },
    {
      Header: t('deals.table.volume'),
      accessor: 'volume',
      width: 120,
      ...{ headerStyle },
      sortable: false,
      filterable: false,
      Cell: (row) => (
        <div style={{ textAlign: 'right' }}>
          {`${decimalFormat(row.value.value)} ${row.value.unit}`}
        </div>
      ),
    },
    {
      Header: t('deals.table.price'),
      accessor: 'price',
      ...columnRight,
      sortable: false,
      filterable: false,
      Cell: (row) => (
        <div style={{ textAlign: 'right' }}>
          {`${decimalFormat(row.value.value, { digitCount: 4, force: true })} ${row.value.unit}`}
        </div>
      ),
    },
    {
      Header: t('execution.transactionView.internalValid'),
      accessor: 'internalValidation.Status',
      ...columnRight,
      sortable: true,
      filterable: true,
      filterMethod: (filter, row)=> {
        if (filter.value === "all"){
          return true;
        }
        return row[filter.id] == filter.value
      },
      Filter: ({ filter, onChange }) => (
        <select
          onChange={event => onChange(event.target.value)}
          style={{ width: "100%" }}
          value={filter ? filter.value : "all"}
        >
          <option value="all">{t('execution.transactionStatus.all')}</option>
          <option value="1">{t('execution.transactionStatus.pending')}</option>
          <option value="2">{t('execution.transactionStatus.validatedInternal')}</option>
          <option value="3">{t('execution.transactionStatus.invalidatedInternal')}</option>
          <option value="4">{t('execution.transactionStatus.validatedClient')}</option>
        </select>
      ),
      Cell: (row) => (
        <div style={{ textAlign: 'right' }}>
          {validText(row.value)}
        </div>
      ),
    },
    {
      Header: null,
      accessor: 'visualize',
      width: 50,
      headerStyle,
      filterable: false,
      sortable: false,
      Cell: (row) => {
        const { contractId, lotId, year, id } = row.value
        return (
          <div style={{ textAlign: 'center' }}>
            <Button
              className="btn-table active"
              onClick={() => {
                setSelected(row.original.id)
                props.onVisualize(Number(contractId), Number(lotId), year, Number(id))
              }}
            >
              <i className="icon-eye" style={{ cursor: 'default' }} />
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <div className="deals-table">
      <ReactTable
        loading={isLoading}
        data={data}
        columns={columns}
        defaultPageSize={20}
        className="-striped"
        LoadingComponent={TableSpinner}
        filterable={true}
        defaultFilterMethod={(filter, row) => {
          const a = String(row[filter.id]).toLowerCase().replace(/\s\s+/g, ' ')
          const b = filter.value.toLowerCase()
          return a.includes(b)
        }}
        sortable={true}
        defaultSorted={[
          {
            id: 'contract',
            desc: false,
          },
          {
            id: 'parcel',
            desc: false,
          },
          {
            id: 'product',
            desc: false,
          },
          {
            id: 'date',
            desc: false,
          },
        ]}
        {...translations(t)}
        getTrProps={(state, rowInfo) => {
          return {
            style: {
              background: rowInfo?.original.id === selected ? 'rgba(33, 33, 90, .2)' : 'unset',
            },
          }
        }}
      />
    </div>
  )
}

export default DealsTable
